import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import { logout } from '../Authorization/actions';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import SmartFormattedRelativeTime from '../../components/SmartFormattedRelativeTime';

const messages = {
  sessionTimeOut: {
    id: 'activity-notification.sessionTimeOut',
    defaultMessage: 'Session Timeout',
  },
  sessionTimeOutMessage: {
    id: 'activity-notification.sessionTimeOutMessage',
    defaultMessage: 'Your session will expire in {time} due to inactivity.',
  },
  stayLoggedIn: {
    id: 'activity-notification.stayLoggedIn',
    defaultMessage: 'STAY LOGGED IN',
  },
  logOut: {
    id: 'activity-notification.logOut',
    defaultMessage: 'LOG OUT',
  },
};

const localStorageKey = 'match-idle-lastactive';
function getLocalStorageTime() {
  const item = window.localStorage.getItem(localStorageKey);
  try {
    return JSON.parse(item);
  } catch (e) /* istanbul ignore next */ {
    console.log('corrupt local storage time', item); // eslint-disable-line no-console
    return 0;
  }
}

function getLastActive(loginTime) {
  const lastActive = Math.max(
    getLocalStorageTime(),
    loginTime,
  );
  return lastActive;
}

function isStillIdle(loginTime, timeout) {
  const now = Date.now();
  const lastActive = getLastActive(loginTime);
  return (now - lastActive) > timeout;
}

class IdleTimeoutComponent extends React.PureComponent {
  static propTypes = {
    timeout: PropTypes.number.isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        loginTime: PropTypes.number,
      }).isRequired,
    }).isRequired,
    events: PropTypes.arrayOf(PropTypes.string),
    logout: PropTypes.func.isRequired,
  };

  static defaultProps = {
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ],
  };

  constructor(...args) {
    super(...args);
    this.state = {
      openModal: false,
      lastActive: 0,
    };
    this._throttledHandleEvent = throttle(this._handleEvent, 1000);
  }

  componentDidMount() {
    this.props.events.forEach((e) => document.addEventListener(e, this._throttledHandleEvent));
    this._handleIdle();
    this._showModal();
  }

  componentWillUnmount() {
    clearTimeout(this.timerId);
    this.props.events.forEach((e) => document.removeEventListener(e, this._throttledHandleEvent));
  }

  _handleIdle = () => {
    const { timeout, current } = this.props;
    const now = Date.now();
    const loginTime = get(current, 'viewer.loginTime', 0);
    const lastActive = getLastActive(loginTime);
    if (isStillIdle(loginTime, timeout)) {
      this.props.logout();
    } else {
      this.setState({ lastActive });
      // another tab has been active, refresh timeout to the appropriate time
      this.timerId = setTimeout(this._handleIdle, timeout - (now - lastActive));
    }
  };

  _handleEvent = () => {
    const { timeout } = this.props;
    const now = Date.now();
    try {
      window.localStorage.setItem(localStorageKey, JSON.stringify(now));
    } catch (e) {
      // safari private browsing throws errors on setting local storage
      /* istanbul ignore next */
      console.log(`Storage Set Failed for key: ${localStorageKey}`, e); // eslint-disable-line no-console
    }
    this.setState({ lastActive: now });
    clearTimeout(this.timerId);
    clearTimeout(this.showModalTimerId);
    this.showModalTimerId = setTimeout(this._showModal, timeout - (2 * 60 * 1000));
    this.timerId = setTimeout(this._handleIdle, timeout);
  };

  _showModal = () => {
    const { timeout, current } = this.props;
    const loginTime = get(current, 'viewer.loginTime', 0);
    const now = Date.now();
    const lastActive = getLastActive(loginTime);
    if (isStillIdle(loginTime, (timeout - 2 * 60 * 1000))) {
      this.setState({ openModal: true, lastActive });
    } else {
      this.showModalTimerId = setTimeout(this._showModal, timeout - (2 * 60 * 1000) - (now - lastActive));
    }
  };

  logOutHandle = () => {
    this.props.logout();
  }

  stayLoggedIn = () => {
    this.setState({ openModal: false });
    this._handleEvent();
  }

  getTimeoutCountdown = () => Date.now() + (this.props.timeout - (Date.now() - this.state.lastActive));

  /* eslint-disable class-methods-use-this */
  render() {
    return (
      <Modal
        closeOnEscape={false}
        closeOnDimmerClick={false}
        open={this.state.openModal}
        size="tiny"
        dimmer="blurring"
      >
        <Modal.Header as="h4">
          <FormattedMessage {...messages.sessionTimeOut} />
        </Modal.Header>
        <Modal.Content>
          {/* https://github.com/formatjs/formatjs/pull/1338 */}
          <FormattedMessage
            {...messages.sessionTimeOutMessage}
            values={{
              time: <SmartFormattedRelativeTime value={this.getTimeoutCountdown()} />,
            }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.logOutHandle}>
            <FormattedMessage {...messages.logOut} />
          </Button>
          <Button primary onClick={this.stayLoggedIn}>
            <FormattedMessage {...messages.stayLoggedIn} />
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
  /* eslint-enable class-methods-use-this */
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export { localStorageKey, IdleTimeoutComponent };
export default connect(mapStateToProps, { logout })(IdleTimeoutComponent);
